import { useArchiveCannedWorkTasksMutation } from "@common/api/quoting/canned"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import z from "zod"
import { DeleteDialog } from "@/components/layout/list-table-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"

const searchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export const Route = createFileRoute("/_layout/templates/tasks/delete")({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    await requirePermission(queryClient, "canned_work_package:delete", "/")
    if (search.ids.length === 0) {
      throw redirect({ to: "/templates/tasks", search: true })
    }
  },
  component: DeleteTemplateTasksDialog,
})

function counted(count: number): string {
  return `${formatCount(count)} template task${count === 1 ? "" : "s"}`
}

function DeleteTemplateTasksDialog(): React.ReactElement {
  const { ids } = Route.useSearch()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const remove = useArchiveCannedWorkTasksMutation()

  const close = () =>
    navigate({
      from: Route.fullPath,
      to: "/templates/tasks",
      search: ({ ids: _, ...previous }) => previous,
    })

  return (
    <DeleteDialog
      noun={ids.length === 1 ? "template task" : counted(ids.length)}
      pending={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        remove.mutate(
          { cannedWorkTaskBulkIds: { canned_work_task_ids: ids } },
          {
            onSuccess: ({ archived }) => {
              showToast(
                "Deleted",
                `${counted(archived.length)} deleted.`,
                "success",
              )
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    />
  )
}
